import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, CheckCircle2, Circle } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import { caseLabel } from "@/lib/labels";
import { fmtDate, istDateKey } from "@/lib/time";
import FilterPill from "@/components/FilterPill";
import useRowKeyboardNav from "@/lib/useRowKeyboardNav";

export default function Tasks() {
    const [items, setItems] = useState([]);
    const [cases, setCases] = useState([]);
    const [staff, setStaff] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [editSaving, setEditSaving] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", case_id: "", assigned_to: "", due_date: "", priority: "medium" });
    const [saving, setSaving] = useState(false);
    const [params] = useSearchParams();
    const filter   = params.get("filter");      // urgent | overdue | pending
    const priority = params.get("priority");    // high
    const openParam = params.get("open");        // task id to auto-open edit modal

    const load = async () => {
        try {
            const [t, c, u] = await Promise.all([api.get("/tasks"), api.get("/cases"), api.get("/users")]);
            setItems(t.data); setCases(c.data); setStaff(u.data.filter(x => x.role !== "client_portal"));
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await api.post("/tasks", form);
            toast.success("Task added");
            setOpen(false); setForm({ title: "", description: "", case_id: "", assigned_to: "", due_date: "", priority: "medium" });
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const toggle = async (t) => {
        const status = t.status === "done" ? "open" : "done";
        const reason = window.prompt(`Reason for marking task as ${status} (mandatory · logged to audit):`);
        if (!reason || reason.trim().length < 3) {
            toast.error("Status change requires a reason of at least 3 characters");
            return;
        }
        try {
            await api.patch(`/tasks/${t.id}`, { status }, { headers: { "X-Change-Reason": reason.trim() } });
            load();
            toast.success("Task updated · logged to audit trail");
        } catch (e) { toast.error(apiError(e)); }
    };

    const openEdit = (t) => {
        setEdit({
            id: t.id,
            title: t.title || "",
            description: t.description || "",
            case_id: t.case_id || "",
            assigned_to: t.assigned_to || "",
            due_date: t.due_date || "",
            priority: t.priority || "medium",
            reason: "",
        });
    };
    const saveEdit = async (e) => {
        e.preventDefault();
        if (!edit?.reason || edit.reason.trim().length < 3) {
            toast.error("Reason required (min 3 chars) — every edit is logged to the audit trail."); return;
        }
        setEditSaving(true);
        try {
            const { id, reason, ...patch } = edit;
            await api.patch(`/tasks/${id}`, patch, { headers: { "X-Change-Reason": reason.trim() } });
            toast.success("Task updated · logged to audit trail");
            setEdit(null);
            load();
        } catch (err) { toast.error(apiError(err)); }
        finally { setEditSaving(false); }
    };

    const visibleItems = useMemo(() => {
        const today = istDateKey(new Date());
        const in3d  = (() => { const d = new Date(); d.setDate(d.getDate() + 3); return istDateKey(d); })();
        return items.filter(t => {
            if (priority === "high"   && (!["high", "urgent"].includes(t.priority) || t.status === "done")) return false;
            if (filter === "urgent"   && !( ["high","urgent"].includes(t.priority) && t.status !== "done" && t.due_date && t.due_date >= today && t.due_date <= in3d )) return false;
            if (filter === "overdue"  && !( t.status !== "done" && t.due_date && t.due_date < today )) return false;
            if (filter === "pending"  && t.status === "done") return false;
            return true;
        });
    }, [items, filter, priority]);

    const filterLabel =
        filter === "urgent"   ? "Urgent · due in next 3 days" :
        filter === "overdue"  ? "Overdue only" :
        filter === "pending"  ? "Pending only" :
        priority === "high"   ? "High priority only" : null;
    const filterTone = filter === "overdue" ? "danger" : filter === "urgent" ? "danger" : priority === "high" ? "indigo" : "warning";

    const { getRowProps } = useRowKeyboardNav({ items: visibleItems, onOpen: openEdit, disabled: !!edit || open });

    // Auto-open the edit modal if ?open=<id> is in the URL (used by My Week deep links)
    useEffect(() => {
        if (!openParam || edit) return;
        const t = items.find(x => x.id === openParam);
        if (t) openEdit(t);
    }, [openParam, items]);    // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <PageHeader
                title="Tasks"
                subtitle="Drafts due, calls to make, follow-ups owed — kept in one column."
                action={<GoldButton onClick={() => setOpen(true)} data-testid="tasks-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>New task</GoldButton>}
            />

            {filterLabel && (
                <div className="mb-4 flex items-center gap-2" data-testid="tasks-filter-banner">
                    <FilterPill label={filterLabel} testId={`tasks-pill-${filter || priority}`} tone={filterTone}/>
                    <span className="text-xs text-muted-foreground">{visibleItems.length} task{visibleItems.length === 1 ? "" : "s"}</span>
                </div>
            )}

            {visibleItems.length === 0 ? (
                <EmptyState title={filterLabel ? `No matching tasks` : "No tasks"} subtitle={filterLabel ? "Try clearing the filter to see everything." : "Capture follow-ups and assignments here."} action={<GoldButton onClick={() => setOpen(true)} data-testid="tasks-empty-add">Add task</GoldButton>}/>
            ) : (
                <div className="border border-border divide-y divide-border">
                    {visibleItems.map((t, idx) => (
                        <div key={t.id} {...getRowProps(idx)} className="px-5 py-4 flex items-center gap-4 hover:bg-muted/50 transition-colors" data-testid={`task-row-${t.id}`}>
                            <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="shrink-0">
                                {t.status === "done"
                                    ? <CheckCircle2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
                                    : <Circle className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />}
                            </button>
                            <button type="button" onClick={() => openEdit(t)}
                                    data-testid={`task-open-${t.id}`}
                                    className="flex-1 min-w-0 text-left cursor-pointer">
                                <div className={`text-sm ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                                {t.description && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
                            </button>
                            <button type="button" onClick={() => openEdit(t)}
                                    className="text-xs text-muted-foreground hidden sm:block hover:text-foreground">
                                {t.due_date ? fmtDate(t.due_date) : "—"}
                            </button>
                            <StatusBadge status={t.priority}/>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="New task" testId="task-modal">
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Title"><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="task-title-input"/></Field>
                    <Field label="Description"><Textarea rows="2" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}/></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Litigation">
                            <Select value={form.case_id} onChange={e => setForm({ ...form, case_id: e.target.value })}
                                options={[{ value: "", label: "— optional —" }, ...cases.map(c => ({ value: c.id, label: caseLabel(c) }))]} />
                        </Field>
                        <Field label="Due date"><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}/></Field>
                    </div>
                    <Field label="Assign to">
                        <Select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} data-testid="task-assignee-select"
                            options={[{ value: "", label: "— unassigned —" }, ...staff.map(s => ({ value: s.id, label: `${s.name} · ${s.role.replace(/_/g, " ")}` }))]}/>
                    </Field>
                    <Field label="Priority"><Select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} options={["low", "medium", "high", "urgent"]}/></Field>
                    <div className="flex justify-end pt-2"><GoldButton disabled={saving} data-testid="task-save-btn">{saving ? "Saving…" : "Add task"}</GoldButton></div>
                </form>
            </Modal>

            <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit task" testId="task-edit-modal">
                {edit && (
                    <form onSubmit={saveEdit} className="space-y-4">
                        <Field label="Title"><Input required value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} data-testid="task-edit-title"/></Field>
                        <Field label="Description"><Textarea rows="2" value={edit.description} onChange={e => setEdit({ ...edit, description: e.target.value })} data-testid="task-edit-desc"/></Field>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Litigation">
                                <Select value={edit.case_id} onChange={e => setEdit({ ...edit, case_id: e.target.value })}
                                    options={[{ value: "", label: "— optional —" }, ...cases.map(c => ({ value: c.id, label: caseLabel(c) }))]}/>
                            </Field>
                            <Field label="Due date"><Input type="date" value={edit.due_date} onChange={e => setEdit({ ...edit, due_date: e.target.value })} data-testid="task-edit-due"/></Field>
                        </div>
                        <Field label="Assign to">
                            <Select value={edit.assigned_to} onChange={e => setEdit({ ...edit, assigned_to: e.target.value })}
                                options={[{ value: "", label: "— unassigned —" }, ...staff.map(s => ({ value: s.id, label: `${s.name} · ${s.role.replace(/_/g, " ")}` }))]}/>
                        </Field>
                        <Field label="Priority"><Select value={edit.priority} onChange={e => setEdit({ ...edit, priority: e.target.value })} options={["low", "medium", "high", "urgent"]} data-testid="task-edit-priority"/></Field>
                        <Field label="Reason for change (min 3 chars · logged to audit)">
                            <Input required minLength={3} value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })}
                                   placeholder="e.g. Client requested deadline change" data-testid="task-edit-reason"/>
                        </Field>
                        <div className="flex justify-end pt-2"><GoldButton disabled={editSaving} data-testid="task-edit-save">{editSaving ? "Saving…" : "Save changes"}</GoldButton></div>
                    </form>
                )}
            </Modal>
        </>
    );
}